import * as Sentry from '@sentry/react'
import { platform } from '../../device/platform'
import { IDiscoveryResponse } from '../../../interfaces/MFE/IDiscoveryAPI'
import fetchMicrofrontsDiscovery from '../../versions/discovery'
import { filesystem } from '..'
import { getDiscoveryFilePath } from '../../../consts/filesystem/PATHS.cts'
import { getUserIdByToken } from '../../session/get-user-from-token'
import defaultDiscovery from '../../../../../public/assets/constants/default-discovery.json'
import { getAllLocalMfes } from '../../../hooks/ModuleFederation/local-mfe-config'

export const DEFAULT_DEV_FEATURE_ID = '-1'
export const LOCAL_DEV_MFE_BASE_PATH = 'local'

// VER CASUISTICA DE LEER MODULOS:
// 1 - teniendo un initialToken --> leer usuario --> buscar modulos de archivo --> DONE
// 2 - sin initialToken --> buscar cualquier archivo de cualquier usuario
// 3 - sin initialToken de ningun usuario. Primera vez sin conexion.

export default async function initMicrofrontendsDiscovery (initialToken?: string): Promise<IDiscoveryResponse> {

  return await fetchMicrofrontsDiscovery()
    .then(async (res) => {
      Sentry.addBreadcrumb({
        category: 'init.discovery',
        message: `[discovery] API success - ${res.microFrontends.length} modules`,
        level: 'info',
        data: { source: 'api', modulesCount: res.microFrontends.length }
      })
      if (platform !== 'web') {
        if (initialToken) {
          const userId = getUserIdByToken(initialToken)
          filesystem?.write(getDiscoveryFilePath(userId), res.microFrontends).catch(console.error)
        }
      }

      // In development, inject locally configured MFEs that are NOT already in discovery.
      // This covers MFEs that have never been published but are running locally.
      if (process.env.NODE_ENV === 'development') {
        const localMfes = getAllLocalMfes()
        for (const localMfe of localMfes) {
          const alreadyInDiscovery = res.microFrontends.some(
            m => m.mfe_name?.toLowerCase() === localMfe.name.toLowerCase()
          )
          if (!alreadyInDiscovery) {
            res.microFrontends.push({
              version: '0.0.0-local',
              location: `http://localhost:${localMfe.port}/`,
              data: {
                name: {
                  default: localMfe.name
                },
                metadata: {
                  layout: {
                    isFloating: true,
                    isFit: false,
                    isTab: false,
                    isModal: false,
                    isFullscreen: false
                  },
                  layoutMobile: {
                    isBottomsheet: false,
                    isFloating: true,
                    isFit: false,
                    isTab: false,
                    isModal: false,
                    isFullscreen: false
                  },
                  offline: {
                    hasMetadataToDownload: false,
                    isOfflineEnabled: false
                  },
                  config: {
                    hasToggleMapMobile: true,
                    hasClosingIcon: true
                  }
                },
                mfe_id: `local-${localMfe.name.toLowerCase()}`,
                version: '0.0.0-local',
                entry_file: 'remoteEntry.js'
              },
              mfe_id: `local-${localMfe.name.toLowerCase()}`,
              mfe_name: localMfe.name,
              route: `${LOCAL_DEV_MFE_BASE_PATH}/${localMfe.name.toLowerCase()}`
            })
          }
        }
      }

      return res
    })
    .catch(async (err) => {
      Sentry.captureException(err instanceof Error ? err : new Error(`[discovery] API failed: ${err?.message ?? err}`), {
        tags: { init_phase: 'discovery', platform, discovery_source: 'api_failed' },
        extra: { hasToken: !!initialToken, hasFilesystem: filesystem !== undefined }
      })

      if (initialToken && filesystem !== undefined) {
        const modulesSaved = await filesystem.read<IDiscoveryResponse>(getDiscoveryFilePath(getUserIdByToken(initialToken))).catch(() => err)
        if (modulesSaved !== undefined) {
          Sentry.addBreadcrumb({
            category: 'init.discovery',
            message: '[discovery] Fallback: loaded from device filesystem cache',
            level: 'warning',
            data: { source: 'filesystem_cache' }
          })
          return { microFrontends: modulesSaved }
        }
      }

      Sentry.addBreadcrumb({
        category: 'init.discovery',
        message: '[discovery] Fallback: using bundled default-discovery.json',
        level: 'warning',
        data: { source: 'bundled_default' }
      })

      return defaultDiscovery as unknown as IDiscoveryResponse
    })
}
